import moment, { Moment } from 'moment/moment';
import { startCase } from 'lodash/string';
import { Connection, ConnectionSource, LedgerAccount, UsageType } from '../types';

const plaidGuideUrl =
  'https://docs.google.com/document/d/15sD93hPTpb-13xiDCfUUbmTrxhLwSJ8yTzrK_IoLX8s/edit?usp=sharing';
const merchantGuildUrl =
  'https://benchco.atlassian.net/wiki/spaces/OPS2/pages/3139272828/Account+Balancing+Team+Troubleshooting+Merchant+Payroll+Integrations?atlOrigin=eyJpIjoiZmQyYzYzZjRiYTJkNGQ2ZGE5NzAwMDI3MzA3MGJmMTQiLCJwIjoiY29uZmx1ZW5jZS1jaGF0cy1pbnQifQ';

const removedFromSourceMessage = `This account was disconnected at the request of the client. It may be available under a new mapping, please click Edit and re-map them to the correct active ones if possible, or delete the connection and <a class="router-url" href="new-dashboard">ask the client</a> to reconnect.`;
const mappedToBusinessMessage =
  'This account’s type was changed to “Business Account”, but is currently mapped to a Personal Account used for Business ledger. Confirm how the account is used with your client and click “Edit” to ensure the connection is mapped to the correct ledger.';
const mappedToPersonalForBusinessMessage =
  'This account’s type was changed to “Personal Account used for Business”, but is currently mapped to a business ledger. Confirm how the account is used with your client and click “Edit” to ensure the connection is mapped to the correct ledger.';
const mappedToPersonalMessage =
  'This account’s type was changed to “Not used for this Business”, but is currently mapped to a Personal Account used for Business ledger. Confirm this change with your client and click “Edit” to remove the ledger mapping if this account is no longer used.';
const missingBalanceMessage = (ledgerId: string) =>
  `Ensure there is at least one statement closing balance within the past 90 days for this ledger. You can check this in ` +
  `<a class="router-url" href="new-folders/statements/${moment().year()}/ledger/${ledgerId}">Folders</a>, ` +
  `or enter a new balance through <a class="router-url" href="document-entry">Document Entry</a>.`;

const transactionsDelayedMessage = (formattedSource: string) =>
  `Transaction updates are currently delayed for this connection. ${formattedSource} is aware of this issue. Please click the sync button for this connection and allow up to 5 days for this issue to resolve by itself. ` +
  `Reconcile this ledger with a bank statement if it is blocking your work.`;

const getLatestBalanceDate = (
  latestValidBalanceDate: Moment | undefined | null,
  latestInvalidBalanceDate: Moment | undefined | null,
): Moment | undefined => {
  if (latestValidBalanceDate && latestInvalidBalanceDate) {
    return moment.max(latestValidBalanceDate, latestInvalidBalanceDate);
  } else if (latestValidBalanceDate) {
    return moment(latestValidBalanceDate);
  } else if (latestInvalidBalanceDate) {
    return moment(latestInvalidBalanceDate);
  } else {
    return undefined;
  }
};

const invalidMessage = `The balance from the data source could not be verified, the connection may need to be re-synced or reset. Please complete the steps outlined in <a href="${merchantGuildUrl}">this guide</a> before requesting any statements.`;

const unverifiedMessage = (formattedSource: string) =>
  `The balance from ${formattedSource} may require re-anchoring or further validation for missing entries. Please complete the steps outlined in <a href="${
    formattedSource === 'Plaid' ? plaidGuideUrl : merchantGuildUrl
  }">this guide</a> before requesting any statements.`;

const noTransactionsReceivedMessage = (date: Moment, formattedSource: string) =>
  `We have not received any new transactions from ${formattedSource} since ` +
  `${date.format('YYYY-MM-DD')}. No action is required on your part. ` +
  `The system will automatically fetch transactions as they become available.`;
const getLedgerErrorMessage = (ledger: LedgerAccount, connection: Connection) => {
  const account = connection.accounts.find(account => account.ledger?.id === ledger.id);

  const latestValidBalanceDate = account?.ledger?.latestValidBalance?.map(balance =>
    balance.date ? moment(balance.date) : undefined,
  )[0];
  const referenceBalanceDate = account?.ledger?.referenceBalance?.map(balance =>
    balance.date ? moment(balance.date) : undefined,
  )[0];

  const latestInvalidBalanceDate = account?.ledger?.latestInvalidBalance?.map(balance =>
    balance.date ? moment(balance.date) : undefined,
  )[0];

  const lastEndOfMonthDay = moment().date(0).startOf('day');

  const isPBCategory = ledger.category?.id === '9';
  if (account?.isRemovedFromSource) {
    return removedFromSourceMessage;
  }

  if (account?.clientUsageType === UsageType[UsageType.BUSINESS] && isPBCategory) {
    return mappedToBusinessMessage;
  } else if (
    account?.clientUsageType === UsageType[UsageType.PERSONAL_USED_FOR_BUSINESS] &&
    !isPBCategory
  ) {
    return mappedToPersonalForBusinessMessage;
  } else if (account?.clientUsageType === UsageType[UsageType.PERSONAL] && isPBCategory) {
    return mappedToPersonalMessage;
  }

  if (
    connection.source === ConnectionSource[ConnectionSource.SQUARE] ||
    connection.source === ConnectionSource[ConnectionSource.STRIPE]
  ) {
    if (!latestValidBalanceDate || !referenceBalanceDate) {
      return invalidMessage;
    } else {
      return null;
    }
  }

  if (!latestValidBalanceDate || latestValidBalanceDate.isBefore(lastEndOfMonthDay)) {
    // while technically there should be no valid balance if there is no ref, that enforcement is up to FDS and there's no reason for the UI to disallow it
    if (!latestValidBalanceDate && !referenceBalanceDate) {
      return missingBalanceMessage(ledger.id);
    }

    const formattedSource = startCase(connection.source.toLowerCase());

    const fiveDaysAgo = moment().subtract(5, 'days');

    const latestBalanceDate = getLatestBalanceDate(
      latestValidBalanceDate,
      latestInvalidBalanceDate,
    );

    if (
      (!latestBalanceDate && !latestInvalidBalanceDate) ||
      (latestBalanceDate && latestBalanceDate.isBefore(fiveDaysAgo))
    ) {
      return transactionsDelayedMessage(formattedSource);
    }

    const validOrRefDate = latestValidBalanceDate || referenceBalanceDate;

    if (validOrRefDate && latestInvalidBalanceDate && validOrRefDate < latestInvalidBalanceDate) {
      return unverifiedMessage(formattedSource);
    }

    return noTransactionsReceivedMessage(
      moment(connection.accounts[0].sourceSyncDateTime),
      formattedSource,
    );
  }

  return null;
};

export default getLedgerErrorMessage;
